import { HttpError, type Schemas, api } from '@/api/client'
import { FormField } from '@/components/shared/FormField'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { useToast } from '@/components/ui/toast'
import { hasPermission } from '@/hooks/usePermission'
import { useIsReadOnly } from '@/hooks/useSubscription'
import { cn } from '@/lib/cn'
import { queryClient } from '@/lib/query'
import { useAuthStore } from '@/stores/auth.store'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createRoute, redirect } from '@tanstack/react-router'
import { motion } from 'framer-motion'
import {
  Banknote,
  BarChart3,
  Building2,
  Calculator,
  Check,
  FileText,
  Globe,
  ImagePlus,
  Loader2,
  type LucideIcon,
  Mail,
  MapPin,
  Package,
  Phone,
  Shield,
  Sparkles,
  Trash2,
  Truck,
  UserCheck,
  Users,
  Warehouse,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { Route as settingsLayoutRoute } from './layout'

const MAX_LOGO_BYTES = 2 * 1024 * 1024
const ACCEPTED_LOGO_MIME = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml']

const orgSchema = z.object({
  name: z.string().min(2).max(100),
  ninea: z.string().max(40).optional().or(z.literal('')),
  rc: z.string().max(40).optional().or(z.literal('')),
  tax_rate: z.coerce.number().min(0).max(100),
  address_line: z.string().max(200).optional().or(z.literal('')),
  city: z.string().max(100).optional().or(z.literal('')),
  postal_code: z.string().max(20).optional().or(z.literal('')),
  website: z.string().max(200).optional().or(z.literal('')),
  contact_email: z.string().max(200).email().optional().or(z.literal('')),
  contact_phone: z.string().max(40).optional().or(z.literal('')),
  about: z.string().max(2000).optional().or(z.literal('')),
})

type OrgForm = z.infer<typeof orgSchema>

interface ModuleSpec {
  code: string
  icon: LucideIcon
}

const MODULES: ModuleSpec[] = [
  { code: 'quote', icon: FileText },
  { code: 'booking', icon: FileText },
  { code: 'shipment', icon: Package },
  { code: 'customs', icon: Shield },
  { code: 'finance', icon: BarChart3 },
  { code: 'warehouse', icon: Warehouse },
  { code: 'fleet', icon: Truck },
  { code: 'crm', icon: Users },
  { code: 'hr', icon: UserCheck },
  { code: 'payroll', icon: Banknote },
]

const PLAN_VARIANT: Record<string, 'default' | 'info' | 'success' | 'warning'> = {
  starter: 'default',
  pro: 'info',
  enterprise: 'success',
}

function toNullOrTrim(value: string | undefined | null): string | null {
  if (value == null) return null
  const trimmed = value.trim()
  return trimmed === '' ? null : trimmed
}

function orgInitials(name: string): string {
  return (
    name
      .split(/\s+/)
      .map((p) => p[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase() || '—'
  )
}

function SectionHeader({
  icon: Icon,
  title,
  subtitle,
  action,
}: {
  icon: LucideIcon
  title: string
  subtitle?: string
  action?: React.ReactNode
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="flex items-start gap-2.5">
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted text-muted-foreground">
          <Icon className="h-4 w-4" aria-hidden="true" />
        </span>
        <div className="flex flex-col">
          <h2 className="text-sm font-semibold text-foreground">{title}</h2>
          {subtitle ? <p className="text-xs text-muted-foreground">{subtitle}</p> : null}
        </div>
      </div>
      {action}
    </div>
  )
}

function LogoBlock({
  logoUrl,
  orgName,
  canEdit,
  size = 'sm',
}: {
  logoUrl: string | null | undefined
  orgName: string
  canEdit: boolean
  size?: 'sm' | 'lg'
}) {
  const { t } = useTranslation('settings')
  const { t: tCommon } = useTranslation('common')
  const toast = useToast()
  const inputRef = useRef<HTMLInputElement>(null)

  const uploadMutation = useMutation({
    mutationFn: (file: File) => api.org.uploadLogo(file),
    onSuccess: () => {
      toast.show({ title: t('organization.logo.uploaded'), variant: 'success' })
      void queryClient.invalidateQueries({ queryKey: ['org'] })
    },
    onError: (err) => {
      const message = err instanceof HttpError ? err.message : tCommon('error_generic')
      toast.show({
        title: t('organization.logo.upload_failed'),
        description: message,
        variant: 'error',
      })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => api.org.deleteLogo(),
    onSuccess: () => {
      toast.show({ title: t('organization.logo.removed'), variant: 'success' })
      void queryClient.invalidateQueries({ queryKey: ['org'] })
    },
    onError: (err) => {
      const message = err instanceof HttpError ? err.message : tCommon('error_generic')
      toast.show({
        title: t('organization.logo.remove_failed'),
        description: message,
        variant: 'error',
      })
    },
  })

  const pick = (files: FileList | null) => {
    const file = files?.[0]
    if (!file) return
    if (!ACCEPTED_LOGO_MIME.includes(file.type)) {
      toast.show({ title: t('organization.logo.invalid_type'), variant: 'error' })
      return
    }
    if (file.size > MAX_LOGO_BYTES) {
      toast.show({ title: t('organization.logo.too_large'), variant: 'error' })
      return
    }
    uploadMutation.mutate(file)
  }

  const busy = uploadMutation.isPending || deleteMutation.isPending
  const boxSize = size === 'lg' ? 'h-24 w-24 rounded-2xl text-2xl' : 'h-20 w-20 rounded-2xl text-xl'

  return (
    <div className="flex items-start gap-4">
      <div
        className={cn(
          'relative flex shrink-0 items-center justify-center overflow-hidden border border-border bg-gradient-to-br from-foreground to-foreground/80 font-bold text-background shadow-sm',
          boxSize,
        )}
      >
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={orgName}
            className="h-full w-full bg-background object-contain"
            loading="lazy"
          />
        ) : (
          <span>{orgInitials(orgName)}</span>
        )}
        {busy ? (
          <div className="absolute inset-0 flex items-center justify-center bg-black/40">
            <Loader2 className="h-5 w-5 animate-spin text-white" />
          </div>
        ) : null}
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <p className="text-sm font-medium text-foreground">{t('organization.logo.title')}</p>
        <p className="text-xs text-muted-foreground">{t('organization.logo.hint')}</p>
        <div className="flex flex-wrap gap-2 pt-1">
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPTED_LOGO_MIME.join(',')}
            onChange={(e) => pick(e.target.files)}
            className="hidden"
            disabled={!canEdit || busy}
          />
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={!canEdit || busy}
            onClick={() => inputRef.current?.click()}
          >
            <ImagePlus className="mr-1.5 h-4 w-4" />
            {logoUrl ? t('organization.logo.replace') : t('organization.logo.upload')}
          </Button>
          {logoUrl ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={!canEdit || busy}
              onClick={() => deleteMutation.mutate()}
            >
              <Trash2 className="mr-1.5 h-4 w-4" />
              {t('organization.logo.remove')}
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  )
}

function ModuleBadge({ module, active }: { module: ModuleSpec; active: boolean }) {
  const { t } = useTranslation('settings')
  const Icon = module.icon
  return (
    <div
      className={cn(
        'flex items-center gap-2 rounded-xl border px-3 py-2.5 text-sm transition-all',
        active
          ? 'border-emerald-200 bg-emerald-50/50 text-emerald-900 dark:border-emerald-900/60 dark:bg-emerald-950/30 dark:text-emerald-100'
          : 'border-border bg-muted text-muted-foreground',
      )}
    >
      <Icon
        className={cn(
          'h-4 w-4 shrink-0',
          active ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground',
        )}
      />
      <span className="truncate">{t(`organization.modules.${module.code}`)}</span>
      {active ? (
        <Check className="ml-auto h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
      ) : null}
    </div>
  )
}

function OrganizationPage() {
  const { t, i18n } = useTranslation('settings')
  const { t: tCommon } = useTranslation('common')
  const toast = useToast()
  const canEdit = hasPermission(useAuthStore.getState().permissions, 'org:update')
  const readOnly = useIsReadOnly()
  const [websiteError, setWebsiteError] = useState<string | null>(null)

  const orgQuery = useQuery({
    queryKey: ['org'],
    queryFn: () => api.org.get(),
    staleTime: 60_000,
  })

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<OrgForm>({
    resolver: zodResolver(orgSchema),
    defaultValues: {
      name: '',
      ninea: '',
      rc: '',
      tax_rate: 18,
      address_line: '',
      city: '',
      postal_code: '',
      website: '',
      contact_email: '',
      contact_phone: '',
      about: '',
    },
  })

  useEffect(() => {
    if (!orgQuery.data) return
    const o = orgQuery.data
    reset({
      name: o.name,
      ninea: o.ninea ?? '',
      rc: o.rc ?? '',
      tax_rate: o.tax_rate,
      address_line: o.address_line ?? '',
      city: o.city ?? '',
      postal_code: o.postal_code ?? '',
      website: o.website ?? '',
      contact_email: o.contact_email ?? '',
      contact_phone: o.contact_phone ?? '',
      about: o.about ?? '',
    })
  }, [orgQuery.data, reset])

  const mutation = useMutation({
    mutationFn: (body: OrgForm) => {
      const website = toNullOrTrim(body.website)
      if (website && !/^https?:\/\//i.test(website)) {
        setWebsiteError(t('organization.website_invalid'))
        throw new Error('invalid_website')
      }
      setWebsiteError(null)
      return api.org.patch({
        name: body.name,
        ninea: toNullOrTrim(body.ninea),
        rc: toNullOrTrim(body.rc),
        tax_rate: body.tax_rate,
        address_line: toNullOrTrim(body.address_line),
        city: toNullOrTrim(body.city),
        postal_code: toNullOrTrim(body.postal_code),
        website,
        contact_email: toNullOrTrim(body.contact_email),
        contact_phone: toNullOrTrim(body.contact_phone),
        about: toNullOrTrim(body.about),
      } as Schemas['UpdateOrgRequest'])
    },
    onSuccess: (updated) => {
      toast.show({ title: t('organization.saved'), variant: 'success' })
      void queryClient.invalidateQueries({ queryKey: ['org'] })
      void queryClient.invalidateQueries({ queryKey: ['auth', 'me'] })
      reset({
        name: updated.name,
        ninea: updated.ninea ?? '',
        rc: updated.rc ?? '',
        tax_rate: updated.tax_rate,
        address_line: updated.address_line ?? '',
        city: updated.city ?? '',
        postal_code: updated.postal_code ?? '',
        website: updated.website ?? '',
        contact_email: updated.contact_email ?? '',
        contact_phone: updated.contact_phone ?? '',
        about: updated.about ?? '',
      })
    },
    onError: (error) => {
      if (error instanceof Error && error.message === 'invalid_website') return
      const message = error instanceof HttpError ? error.message : tCommon('error_generic')
      toast.show({ title: t('organization.save_failed'), description: message, variant: 'error' })
    },
  })

  if (orgQuery.isPending) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-40 rounded-2xl" />
        <Skeleton className="h-64" />
        <Skeleton className="h-64" />
      </div>
    )
  }

  const org = orgQuery.data
  if (!org) return null

  const modules = org.license.modules ?? []
  const activeCount = modules.filter((m) => MODULES.some((spec) => spec.code === m)).length
  const limits = org.license.limits ?? {}
  const createdLabel = new Date(org.created_at).toLocaleDateString(i18n.language || 'fr', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  })

  return (
    <div className="flex flex-col gap-6">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
      >
        <Card className="overflow-hidden">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-muted via-muted to-white" />
            <div
              aria-hidden="true"
              className="absolute inset-0 opacity-40"
              style={{
                backgroundImage:
                  'radial-gradient(circle at 20% 20%, rgba(161,161,170,0.15), transparent 40%), radial-gradient(circle at 80% 0%, rgba(161,161,170,0.1), transparent 35%)',
              }}
            />
            <div className="relative flex flex-col gap-5 p-6 md:flex-row md:items-center md:justify-between md:p-8">
              <div className="flex min-w-0 items-center gap-4">
                <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-2xl border border-border bg-background shadow-sm">
                  {org.logo_url ? (
                    <img
                      src={org.logo_url}
                      alt={org.name}
                      className="h-full w-full object-contain"
                      loading="lazy"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-foreground to-foreground/80 text-2xl font-bold text-background">
                      {orgInitials(org.name)}
                    </div>
                  )}
                </div>
                <div className="flex min-w-0 flex-col gap-1.5">
                  <h1 className="truncate text-2xl font-bold text-foreground md:text-3xl">
                    {org.name}
                  </h1>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge
                      variant={PLAN_VARIANT[org.license.plan] ?? 'default'}
                      className="uppercase"
                      withDot
                    >
                      {org.license.plan}
                    </Badge>
                    <span className="font-mono text-xs text-muted-foreground">{org.slug}</span>
                    {org.website ? (
                      <a
                        href={org.website}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                      >
                        <Globe className="h-3 w-3" />
                        {org.website.replace(/^https?:\/\//, '')}
                      </a>
                    ) : null}
                  </div>
                </div>
              </div>
              <div className="flex flex-col items-start gap-1 text-xs text-muted-foreground md:items-end md:text-right">
                <span>
                  {t('organization.active_modules_count', {
                    count: activeCount,
                    defaultValue: `${activeCount} modules actifs`,
                  })}
                </span>
                <span className="font-mono text-muted-foreground">
                  {org.country} · {org.timezone}
                </span>
                <span className="text-muted-foreground">
                  {t('organization.subscription.since')} {createdLabel}
                </span>
              </div>
            </div>
          </div>
        </Card>
      </motion.div>

      <form
        onSubmit={handleSubmit((data) => mutation.mutate(data))}
        className="flex flex-col gap-6"
        noValidate
      >
        <Card className="transition-shadow hover:shadow-sm">
          <CardContent className="flex flex-col gap-5 p-5">
            <SectionHeader
              icon={Building2}
              title={t('organization.section_identity')}
              subtitle={t('organization.section_identity_desc')}
            />
            <LogoBlock logoUrl={org.logo_url} orgName={org.name} canEdit={canEdit} />
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <FormField
                label={t('organization.name')}
                required
                htmlFor="org-name"
                error={errors.name?.message}
                className="md:col-span-2"
              >
                <Input
                  id="org-name"
                  disabled={!canEdit}
                  {...register('name')}
                  aria-invalid={Boolean(errors.name) || undefined}
                />
              </FormField>

              <FormField
                label={t('organization.slug')}
                htmlFor="org-slug"
                hint={t('organization.slug_hint')}
              >
                <Input id="org-slug" value={org.slug} readOnly disabled className="font-mono" />
              </FormField>

              <FormField label={t('organization.country')} htmlFor="org-country">
                <Input id="org-country" value={org.country} readOnly disabled />
              </FormField>

              <FormField
                label={t('organization.about')}
                htmlFor="org-about"
                hint={t('organization.about_hint')}
                className="md:col-span-2"
              >
                <Textarea
                  id="org-about"
                  rows={3}
                  disabled={!canEdit}
                  placeholder={t('organization.about_placeholder')}
                  {...register('about')}
                />
              </FormField>
            </div>
          </CardContent>
        </Card>

        <Card className="transition-shadow hover:shadow-sm">
          <CardContent className="flex flex-col gap-5 p-5">
            <SectionHeader
              icon={MapPin}
              title={t('organization.section_contact')}
              subtitle={t('organization.section_contact_desc')}
            />
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <FormField
                label={t('organization.address_line')}
                htmlFor="org-address"
                className="md:col-span-2"
              >
                <Input
                  id="org-address"
                  disabled={!canEdit}
                  placeholder={t('organization.address_placeholder')}
                  {...register('address_line')}
                />
              </FormField>

              <FormField label={t('organization.city')} htmlFor="org-city">
                <Input id="org-city" disabled={!canEdit} {...register('city')} />
              </FormField>

              <FormField label={t('organization.postal_code')} htmlFor="org-postal">
                <Input id="org-postal" disabled={!canEdit} {...register('postal_code')} />
              </FormField>

              <FormField
                label={t('organization.website')}
                htmlFor="org-website"
                error={websiteError ?? undefined}
                hint={t('organization.website_hint')}
              >
                <div className="relative">
                  <Globe className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="org-website"
                    type="url"
                    className="pl-9"
                    disabled={!canEdit}
                    placeholder="https://..."
                    {...register('website')}
                  />
                </div>
              </FormField>

              <FormField
                label={t('organization.contact_email')}
                htmlFor="org-email"
                error={errors.contact_email?.message}
              >
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="org-email"
                    type="email"
                    className="pl-9"
                    disabled={!canEdit}
                    placeholder="contact@entreprise.sn"
                    {...register('contact_email')}
                  />
                </div>
              </FormField>

              <FormField label={t('organization.contact_phone')} htmlFor="org-phone">
                <div className="relative">
                  <Phone className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="org-phone"
                    type="tel"
                    className="pl-9"
                    disabled={!canEdit}
                    placeholder="+221 77 123 45 67"
                    {...register('contact_phone')}
                  />
                </div>
              </FormField>
            </div>
          </CardContent>
        </Card>

        <Card className="transition-shadow hover:shadow-sm">
          <CardContent className="flex flex-col gap-5 p-5">
            <SectionHeader icon={Calculator} title={t('organization.section_legal')} />
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <FormField label={t('organization.ninea')} htmlFor="org-ninea">
                <Input
                  id="org-ninea"
                  disabled={!canEdit}
                  className="font-mono"
                  placeholder="0012345678"
                  {...register('ninea')}
                />
              </FormField>

              <FormField label={t('organization.rc')} htmlFor="org-rc">
                <Input id="org-rc" disabled={!canEdit} className="font-mono" {...register('rc')} />
              </FormField>

              <FormField
                label={t('organization.currency')}
                htmlFor="org-currency"
                hint={t('organization.currency_hint')}
              >
                <Input
                  id="org-currency"
                  value={org.currency}
                  readOnly
                  disabled
                  className="font-mono"
                />
              </FormField>

              <FormField
                label={t('organization.tax_rate')}
                htmlFor="org-tax"
                error={errors.tax_rate?.message}
              >
                <div className="relative">
                  <Input
                    id="org-tax"
                    type="number"
                    step="0.01"
                    disabled={!canEdit}
                    className="pr-8"
                    {...register('tax_rate')}
                    aria-invalid={Boolean(errors.tax_rate) || undefined}
                  />
                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                    %
                  </span>
                </div>
              </FormField>

              <FormField label={t('organization.timezone')} htmlFor="org-tz">
                <Input id="org-tz" value={org.timezone} readOnly disabled className="font-mono" />
              </FormField>

              <FormField label={t('organization.locale')} htmlFor="org-locale">
                <Input id="org-locale" value={org.locale.toUpperCase()} readOnly disabled />
              </FormField>
            </div>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden transition-shadow hover:shadow-sm">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -right-24 -top-24 h-56 w-56 rounded-full bg-primary/5 blur-3xl"
          />
          <CardContent className="relative flex flex-col gap-5 p-5">
            <SectionHeader
              icon={Sparkles}
              title={t('organization.subscription.title')}
              subtitle={t('organization.subscription.subtitle')}
              action={
                <Badge
                  variant={PLAN_VARIANT[org.license.plan] ?? 'default'}
                  className="uppercase"
                  withDot
                >
                  {org.license.plan}
                </Badge>
              }
            />

            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="rounded-xl border border-border bg-background p-4">
                <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                  {t('organization.subscription.active_modules')}
                </dt>
                <dd className="mt-1 text-2xl font-bold tabular-nums text-foreground">
                  {activeCount}
                  <span className="ml-1 text-sm font-normal text-muted-foreground">
                    / {MODULES.length}
                  </span>
                </dd>
              </div>
              <div className="rounded-xl border border-border bg-background p-4">
                <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                  {t('organization.subscription.users_limit')}
                </dt>
                <dd className="mt-1 text-2xl font-bold tabular-nums text-foreground">
                  {limits.users ?? '∞'}
                </dd>
              </div>
              <div className="rounded-xl border border-border bg-background p-4">
                <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                  {t('organization.subscription.since')}
                </dt>
                <dd className="mt-1 text-sm font-medium text-foreground">{createdLabel}</dd>
              </div>
            </dl>

            <div>
              <p className="mb-2 text-sm font-medium text-foreground">
                {t('organization.active_modules')}
              </p>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-3">
                {MODULES.map((m) => (
                  <ModuleBadge key={m.code} module={m} active={modules.includes(m.code)} />
                ))}
              </div>
            </div>

            <p className="rounded-xl border border-dashed border-border bg-muted p-3 text-xs text-muted-foreground">
              {t('organization.contact_support')}
            </p>
          </CardContent>
        </Card>

        {canEdit ? (
          <div className="sticky bottom-0 -mx-4 flex justify-end border-t border-border bg-background px-4 py-3 backdrop-blur md:-mx-6 md:px-6">
            <div className="flex items-center gap-3">
              {isDirty ? (
                <span className="text-xs text-muted-foreground">
                  {t('organization.unsaved_changes')}
                </span>
              ) : null}
              <Button type="submit" disabled={readOnly || !isDirty} loading={mutation.isPending}>
                {t('organization.save')}
              </Button>
            </div>
          </div>
        ) : null}
      </form>
    </div>
  )
}

export const Route = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: '/settings/organization',
  beforeLoad: () => {
    const perms = useAuthStore.getState().permissions
    if (!hasPermission(perms, 'org:read')) {
      throw redirect({ to: '/settings/profile' })
    }
  },
  component: OrganizationPage,
})
